<template>
  <div tabindex="0" ref="tag_selector_target" class="full-width">
    <q-splitter
      v-if="full"
      v-model="splitterModel.outerModel"
      :style="`${mHeight ? `height: ${mHeight}px;` : 'height: 50vh;'} width: 50vw;`"
    >
      <template v-slot:before>
        <div style="height: 100%; overflow: hidden;">
          <div class="q-pa-xs q-mb-xs text-center tag-header">
            {{ tagSegmentLabel }}
          </div>
          <div class="q-px-xs q-py-none col" :style="`height: calc(100% - 35px); overflow: auto;`">
            <variable-list ref="simple_selector_list" :elements="tags_filtered.AllSystemTags" v-model="theModel"
              :allow-multiple="true" :show-checkbox="true" :allow-wrap="true"
              style="padding-left: 4px;"
              :model-type="1" :viewport="$refs.tag_selector_target" key-prefix="alltag_" key-id="Id">
              <template v-slot:default="slotProps">
                <simple-element :element="slotProps.item" :elementType="1" :filter_string="filter_string" />
              </template>
            </variable-list>
          </div>
        </div>
      </template>

      <template v-slot:after>
        <q-splitter
          class="full-width"
          v-model="splitterModel.innerModel"
          horizontal
        >
          <template v-slot:before>
            <div class="q-px-xs q-py-none" style="height: 100%; overflow: hidden;">
              <div class="q-pa-xs q-mb-xs text-center tag-header">
                {{ $t('top_tags_user') }}
              </div>
              <div class="q-px-xs q-py-none col" :style="`height: calc(100% - 35px); overflow: auto;`">
                <variable-list ref="simple_selector_list" :elements="tags_filtered.OftenUsedByUserTags" v-model="theModel"
                  :allow-multiple="true" :show-checkbox="true" :allow-wrap="true"
                  style="padding-left: 4px;"
                  :model-type="1" :viewport="$refs.tag_selector_target" key-prefix="ofusertag_" key-id="Id">
                  <template v-slot:default="slotProps">
                    <simple-element :element="slotProps.item" :elementType="1" :filter_string="filter_string" />
                  </template>
                </variable-list>
              </div>
            </div>
          </template>

          <template v-slot:after>
            <div class="q-px-xs q-py-none" style="height: 100%; overflow: hidden;">
              <div class="q-pa-xs q-mb-xs text-center tag-header">
                {{ $t('top_tags_entity') }}
              </div>
              <div class="q-px-xs q-py-none col" :style="`height: calc(100% - 35px); overflow: auto;`">
                <variable-list ref="simple_selector_list" :elements="tags_filtered.OftenUsedForEntityTags" v-model="theModel"
                  :allow-multiple="true" :show-checkbox="true" :allow-wrap="true"
                  style="padding-left: 4px;"
                  :model-type="1" :viewport="$refs.tag_selector_target" key-prefix="ofenttag_" key-id="Id">
                  <template v-slot:default="slotProps">
                    <simple-element :element="slotProps.item" :elementType="1" :filter_string="filter_string" />
                  </template>
                </variable-list>
              </div>
            </div>
          </template>
        </q-splitter>
      </template>
    </q-splitter>

    <div v-else class="full-width" :style="mHeight ? `height: ${mHeight}px; overflow: auto;` : 'height: 100%; overflow: auto;'">
      <variable-list ref="compact_selector_list"
        :elements="tags_filtered.OftenUsedForEntityTags"
        v-model="theModel"
        :allow-multiple="true"
        :show-checkbox="true"
        :allow-wrap="true"
        :viewport="$refs.tag_selector_target"
        style="padding-left: 4px;"
        key-prefix="ofenttag_" key-id="Id">
        <template v-slot:default="slotProps">
          <simple-element :element="slotProps.item" :elementType="1" :filter_string="filter_string" />
        </template>
      </variable-list>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import { TagContainer } from '@/models/interfaces/tags/tag-models';
import { reactive } from 'vue';
import { auxStore } from '@/store/auxillary';
import { rootStore } from '@/store/index';
import { AnalyticsV2ElementType } from '@/models/etc/types';
import VariableList from '@/components/common/basic/VariableList.vue';
import SimpleElement from '@/components/common/basic/data-components/SimpleElement.vue';
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';
import { matchesText } from '../utils/text-filter';
import { mapToEntities, mapFromEntities } from '../composables/useEntityModel';
import { isAllSelected, toggleSelectAll } from '../composables/useSelectAll';

const TAG_SEGMENT_MAP: Record<number, string> = {
  [AnalyticsV2ElementType.Client]: 'tags_of_clients',
  [AnalyticsV2ElementType.Project]: 'tags_of_projects',
  [AnalyticsV2ElementType.Person]: 'tags_of_contacts',
  [AnalyticsV2ElementType.Job]: 'tags_of_jobs',
  [AnalyticsV2ElementType.File]: 'tags_of_files',
};

@Component({
  name: 'TagSelector',
  components: { VariableList, SimpleElement, MostaCheckbox },
})
export default class TagSelector extends Vue {
  @Prop({ default: [] as any[] }) modelValue!: any[];
  @Emit('update:modelValue') emitModelValue(arg: any[]) { return arg; }

  @Prop({ default: undefined }) dataToEntity?: (data: any) => any;
  @Prop({ default: undefined }) dataFromEntity?: (data: any) => any;
  @Prop({ default: '' }) filter_string?: string;
  @Prop({ default: 0 }) mHeight!: number;
  @Prop({ default: false }) full!: boolean;

  splitterModel = reactive({ innerModel: 50, outerModel: 50 });

  @Prop({ default: undefined }) parentIdx!: string | undefined;
  @Prop({ default: -1 }) inputIndex!: number;

  store: any | undefined = undefined;

  tags: TagContainer = reactive({
    AllSystemTags: [],
    OftenUsedByUserTags: [],
    OftenUsedForEntityTags: [],
  });

  tags_filtered: TagContainer = reactive({
    AllSystemTags: [],
    OftenUsedByUserTags: [],
    OftenUsedForEntityTags: [],
  });

  get tagSegmentLabel(): string {
    const mode = rootStore().generalMode;
    return this.$t(TAG_SEGMENT_MAP[mode] ?? 'selector_system_tags');
  }

  created() {
    this.store = auxStore();
    this.tags = reactive(this.store.getCachedTagContainer);
    this.filterTags(this.filter_string || '');
    this.loadData();
  }

  get theModel(): any[] {
    return mapToEntities(this.modelValue, this.dataToEntity);
  }

  set theModel(val: any[]) {
    mapFromEntities(val, this.dataFromEntity, (v) => this.emitModelValue(v));
  }

  get allSelected(): boolean {
    return isAllSelected(this.tags_filtered.OftenUsedForEntityTags, this.theModel);
  }

  onToggleSelectAll() {
    toggleSelectAll(this.$refs.compact_selector_list, this.allSelected);
  }

  @Watch('filter_string')
  filterTags(arg: string) {
    if (arg) {
      const matchFn = (el: any) => matchesText(el.FullName || el.Name, arg);
      this.tags_filtered = reactive({
        AllSystemTags: this.tags.AllSystemTags.filter(matchFn),
        OftenUsedByUserTags: this.tags.OftenUsedByUserTags.filter(matchFn),
        OftenUsedForEntityTags: this.tags.OftenUsedForEntityTags.filter(matchFn),
      });
    } else {
      this.tags_filtered = reactive(this.tags);
    }
  }

  async loadData() {
    if (!this.store) return;
    this.tags = reactive((await this.store.getTagContainer()).data as TagContainer);
    this.filterTags(this.filter_string || '');
  }
}
</script>

<style lang="scss">
.tag-header {
  background-color: $primary;
  color: white;
  font-weight: 500;
}

</style>
